import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as LineSetupActions from './line-setup.actions';
import * as QcoActions from '../qco/qco.actions';
import { TLineSetUpActionType } from './line-setup.models';
import { FactoryFacade } from '../factory/factory.facade';

import { AppNotificationUtilClass, MachineMaintenanceDataService } from '@monorepo/services';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class LineSetupEffects {
  constructor(
    private actions$: Actions,
    private mmDataService: MachineMaintenanceDataService,
    private factoryFacade: FactoryFacade,
    private APIHelper: AppNotificationUtilClass,
    private router: Router,
  ) {}

  fetchFactoryLineWorkstationSummaryList = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.GetFactoryLineWorkstationSummaryList),
      switchMap(() =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return factoryId;
          }),
        ),
      ),
      switchMap((factoryId) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, factoryId] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, factoryId]) =>
        this.mmDataService.getFactoryLineWorkstationSummaryList(factoryId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LineSetupActions.GetFactoryLineWorkstationSummaryListSuccess(res)),
          catchError(() => {
            return of(new LineSetupActions.GetFactoryLineWorkstationSummaryListFailure());
          }),
        ),
      ),
    ),
  );

  addNewWorkstation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.AddNewWorkstation),
      switchMap((action: LineSetupActions.AddNewWorkstation) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.addNewWorkstation(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            switch (action.fetchPayload.fetchContext) {
              case 'WORKSTATION_LISTING':
                return new LineSetupActions.GetAllWorkstationsForProductionArea(action.fetchPayload.locationId);

              case 'LINE_SETUP_TICKET_DETAILS_FETCH':
                return new QcoActions.FetchLineSetupTicketCurrentSetup(action.fetchPayload.locationId);
            }
          }),
          catchError(() => {
            return of(new LineSetupActions.AddNewWorkstationFailure());
          }),
        ),
      ),
    ),
  );

  getAllWorkstationsForProductionArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.GetAllWorkstationsForProductionArea),
      switchMap((action: LineSetupActions.GetAllWorkstationsForProductionArea) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService.getAllWorkstationsForProductionArea(factoryId, action.locationId).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LineSetupActions.GetAllWorkstationsForProductionAreaSuccess(res)),
          catchError(() => {
            return of(new LineSetupActions.GetAllWorkstationsForProductionAreaFailure());
          }),
        ),
      ),
    ),
  );

  assignMachineToWorkstation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.AssignMachineToWorkstation),
      switchMap((action: LineSetupActions.AssignMachineToWorkstation) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.assignMachineToWorkstation(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            switch (action.fetchPayload.fetchContext) {
              case 'WORKSTATION_LISTING':
                return new LineSetupActions.GetAllWorkstationsForProductionArea(action.fetchPayload.locationId);

              case 'LINE_SETUP_TICKET_SUBTASK_LISTING':
                return new QcoActions.GetLineSetupTicketsSubtasks({
                  lineSetupTicketId: action.fetchPayload.lineSetupTicketId,
                });
            }
          }),
          catchError(() => {
            return of(new LineSetupActions.AssignMachineToWorkstationFailure());
          }),
        ),
      ),
    ),
  );

  removeMachineFromWorkstation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.RemoveMachineFromWorkstation),
      switchMap((action: LineSetupActions.RemoveMachineFromWorkstation) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.removeMachineFromWorkstation(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            switch (action.fetchPayload.fetchContext) {
              case 'WORKSTATION_LISTING':
                return new LineSetupActions.GetAllWorkstationsForProductionArea(action.fetchPayload.locationId);

              case 'LINE_SETUP_TICKET_SUBTASK_LISTING':
                return new QcoActions.GetLineSetupTicketsSubtasks({
                  lineSetupTicketId: action.fetchPayload.lineSetupTicketId,
                });
            }
          }),
          catchError(() => {
            return of(new LineSetupActions.RemoveMachineFromWorkstationFailure());
          }),
        ),
      ),
    ),
  );

  replaceMachine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.ReplaceMachine),
      switchMap((action: LineSetupActions.ReplaceMachine) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.replaceMachine(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            switch (action.fetchPayload.fetchContext) {
              case 'WORKSTATION_LISTING': {
                return new LineSetupActions.GetAllWorkstationsForProductionArea(action.fetchPayload.locationId);
              }

              case 'LINE_SETUP_TICKET_SUBTASK_LISTING': {
                return new QcoActions.GetLineSetupTicketsSubtasks({
                  lineSetupTicketId: action.fetchPayload.lineSetupTicketId,
                });
              }

              case 'REDIRECT_MACHINE_ISSUE_TICKET': {
                this.router.navigate(['/ticket-management/machine-issue-tickets'], {
                  queryParams: { ticketStatus: 'IN_REPAIR' },
                });
                return new LineSetupActions.ReplaceMachineSuccess();
              }
            }
          }),
          catchError(() => {
            return of(new LineSetupActions.ReplaceMachineFailure());
          }),
        ),
      ),
    ),
  );

  removeWorkStation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.RemoveWorkstation),
      switchMap((action: LineSetupActions.RemoveWorkstation) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.removeWorkstation(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => {
            switch (action.fetchPayload.fetchContext) {
              case 'WORKSTATION_LISTING':
                return new LineSetupActions.GetAllWorkstationsForProductionArea(action.fetchPayload.locationId);

              case 'LINE_SETUP_TICKET_DETAILS_FETCH':
                return new QcoActions.FetchLineSetupTicketCurrentSetup(action.fetchPayload.locationId);
            }
          }),
          catchError(() => {
            return of(new LineSetupActions.RemoveWorkstationFailure());
          }),
        ),
      ),
    ),
  );

  resetFactoryProductionArea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.ResetFactoryProductionArea),
      switchMap((action: LineSetupActions.ResetFactoryProductionArea) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) =>
        this.mmDataService
          .resetFactoryProductionArea({
            factoryId,
            ...action.payload,
          })
          .pipe(
            finalize(() => {
              this.APIHelper.dismissLoadingV2(loaderEl);
            }),
            map(() => {
              return new LineSetupActions.GetFactoryLineWorkstationSummaryList();
            }),
            catchError(() => {
              return of(new LineSetupActions.ResetFactoryProductionAreaFailure());
            }),
          ),
      ),
    ),
  );

  getFactoryLinesForShiftManagement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.GetFactoryLinesForShiftManagement),
      switchMap(() => from(this.APIHelper.presentLoading()).pipe(take(1))),
      switchMap((loaderEl) =>
        this.mmDataService.getShiftScheduleLines().pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => new LineSetupActions.GetFactoryLinesForShiftManagementSuccess(res)),
          catchError(() => {
            return of(new LineSetupActions.GetAllWorkstationsForProductionAreaFailure());
          }),
        ),
      ),
    ),
  );

  addMachineNeedles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.AddMachineNeedles),
      switchMap((action: LineSetupActions.AddMachineNeedles) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.addMachineNeedles(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => new LineSetupActions.AddMachineNeedlesSuccess()),
          catchError(() => {
            return of(new LineSetupActions.AddMachineNeedlesFailure());
          }),
        ),
      ),
    ),
  );

  removeMachineNeedles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TLineSetUpActionType.RemoveMachineNeedles),
      switchMap((action: LineSetupActions.RemoveMachineNeedles) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.mmDataService.removeMachineNeedles(action.payload).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map(() => new LineSetupActions.RemoveMachineNeedlesSuccess()),
          catchError(() => {
            return of(new LineSetupActions.RemoveMachineNeedlesFailure());
          }),
        ),
      ),
    ),
  );
}
